import { useState } from "react";
import "@/App.css";

function App() {
  const [email, setEmail] = useState("");

  const handleJoinWaitlist = () => {
    if (email) {
      const subject = encodeURIComponent("New Waitlist Signup - ARIV");
      const body = encodeURIComponent(`New waitlist signup:\n\nBusiness Email: ${email}`);
      window.location.href = `mailto:bala@arivlogic.com?subject=${subject}&body=${body}`;
    }
  };

  return (
    <div className="ariv-app selection:bg-gold selection:text-black">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 px-6 py-8 flex justify-between items-center bg-transparent backdrop-blur-sm" data-testid="navigation">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center">
            <span className="text-black font-black text-xl">A</span>
          </div>
          <span className="text-xl font-bold tracking-tighter uppercase">Ariv</span>
        </div>
        
        <div className="hidden md:flex space-x-10 text-[11px] font-black uppercase tracking-[0.2em]">
          <a href="#about" className="text-slate-400 hover:text-gold transition" data-testid="nav-about">About</a>
          <a href="#mechanism" className="text-slate-400 hover:text-gold transition" data-testid="nav-mechanism">How it Works</a>
          <a href="#ethos" className="text-slate-400 hover:text-gold transition" data-testid="nav-ethos">Ethos</a>
        </div>
        
        <button className="text-gold border border-gold px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-gold hover:text-black transition" data-testid="beta-button">
          Limited Beta
        </button>
      </nav>

      {/* Hero Section */}
      <header className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden" data-testid="hero-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold-glow rounded-full blur-[120px] opacity-20 pointer-events-none"></div>
        
        <div className="relative z-10 text-center max-w-5xl">
          <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 px-4 py-2 rounded-full mb-8">
            <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse"></span>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Now selecting design partners</span>
          </div>
          
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-extrabold mb-8 tracking-tighter leading-[0.9] gradient-text" data-testid="hero-title">
            The Office <br /><span className="gold-text italic">That Acts.</span>
          </h1>
          
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed">
            Ariv is your <span className="text-white font-semibold">Autonomous Digital Concierge</span>. We don't suggest. We execute.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button className="btn-gold w-full sm:w-auto px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest" data-testid="apply-button">
              Start your free trial
            </button>
            <button className="w-full sm:w-auto bg-white/5 border border-white/10 px-10 py-5 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-white/10 transition" data-testid="vision-button">
              Our Vision
            </button>
          </div>
          <p className="text-slate-500 text-sm mt-4">No credit card required. Takes 5 minutes to set up.</p>
        </div>
      </header>

      {/* THE MECHANISM Section */}
      <section id="mechanism" className="py-32 px-6 bg-gradient-to-b from-transparent to-white/[0.02]" data-testid="mechanism-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-gold">Operational Logic</span>
            <h2 className="text-4xl font-bold mt-4">How Ariv Works.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="relative">
              <div className="text-6xl font-black text-white/5 absolute -top-10 -left-4">01</div>
              <h4 className="text-xl font-bold mb-4 relative z-10">Connect your tools</h4>
              <p className="text-slate-500 text-sm leading-relaxed">Link Ariv to your existing systems (Slack, Salesforce, Calendar, email, and 500+ integrations).</p>
              <p className="text-slate-400 text-sm leading-relaxed mt-2 italic">Ariv learns your entire tech stack</p>
            </div>
            <div className="relative">
              <div className="text-6xl font-black text-white/5 absolute -top-10 -left-4">02</div>
              <h4 className="text-xl font-bold mb-4 relative z-10">Add your context</h4>
              <p className="text-slate-500 text-sm leading-relaxed">Tell Ariv about your priorities, decision-making style, and high-stakes scenarios.</p>
              <p className="text-slate-400 text-sm leading-relaxed mt-2 italic">E.g. Protect my calendar for deep work. Flag urgent decisions. Brief me on threats.</p>
            </div>
            <div className="relative">
              <div className="text-6xl font-black text-white/5 absolute -top-10 -left-4">03</div>
              <h4 className="text-xl font-bold mb-4 relative z-10">Delegate tasks</h4>
              <p className="text-slate-500 text-sm leading-relaxed">Ariv drafts responses, schedules high-value meetings, and updates your CRM—only surfacing for final approval when necessary.</p>
              <p className="text-slate-400 text-sm leading-relaxed mt-2 italic">E.g. Schedule my Q4 board review and brief me on key agenda items.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features & Sovereign Section */}
      <section className="py-32 px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="glass-card p-10 rounded-[40px]">
            <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-8">
              <i className="fa-solid fa-brain text-gold"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Contextual DNA</h3>
            <p className="text-slate-500 leading-relaxed text-sm">Most AIs forget. Ariv learns your business architecture and internal logic to act as a seamless extension of your brain.</p>
          </div>

          <div className="glass-card p-10 rounded-[40px] border-gold/20 bg-gold-glow">
            <div className="w-12 h-12 bg-gold rounded-2xl flex items-center justify-center mb-8">
              <i className="fa-solid fa-bolt text-black"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4">Action-First Logic</h3>
            <p className="text-slate-300 leading-relaxed text-sm italic">"Don't just tell me what's wrong—fix it."</p>
            <p className="text-slate-500 leading-relaxed text-sm mt-4">Ariv is equipped to interface with your existing stack to execute tasks, not just summarize data.</p>
          </div>

          <div className="glass-card p-10 rounded-[40px] relative overflow-hidden group border-white/10">
            <div className="relative z-10 h-full flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <div className="w-14 h-14 bg-white/5 border border-white/10 rounded-full flex items-center justify-center relative">
                  <div className="absolute inset-0 rounded-full pulse-ring"></div>
                  <i className="fa-solid fa-shield-halved text-gold text-xl"></i>
                </div>
                <span className="text-[9px] font-black uppercase tracking-[0.3em] text-gold">Sovereign Protocol</span>
              </div>
              <h3 className="text-3xl font-bold mb-6 tracking-tight">Sovereign <br /><span className="gold-text">Digital Office</span></h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">Zero-knowledge architecture. Your data remains in your control, processed locally, never used for training external models.</p>
              <div className="mt-auto pt-6 border-t border-white/5 text-[10px] text-slate-500 italic">
                "Proprietary strategy stays proprietary."
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IDEAL PARTNERS Section */}
      <section className="py-20 px-6" data-testid="partners-section">
        <div className="max-w-4xl mx-auto glass-card p-12 rounded-[40px] border-dashed">
          <h3 className="text-center text-sm font-black uppercase tracking-[0.4em] mb-10 text-slate-500">Who we are building for</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 opacity-60">
            <div className="text-center font-bold text-xs uppercase tracking-widest">Solopreneurs</div>
            <div className="text-center font-bold text-xs uppercase tracking-widest">Tech Founders</div>
            <div className="text-center font-bold text-xs uppercase tracking-widest">Family Offices</div>
            <div className="text-center font-bold text-xs uppercase tracking-widest">Households</div>
            <div className="text-center font-bold text-xs uppercase tracking-widest">SMB CEOs</div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="contact" className="py-32 px-6" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-8 gradient-text">Secure Your Office.</h2>
          <p className="text-slate-400 mb-12 text-lg">Secure Early Access— We are inviting <span className="text-white">12 more design partners</span> to test Ariv and get founding member access.</p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-24">
            <input 
              type="email" 
              placeholder="Business email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white/5 border border-white/10 px-6 py-4 rounded-2xl w-full sm:w-80 focus:outline-none focus:border-gold transition text-slate-200"
              data-testid="email-input"
            />
            <button 
              onClick={handleJoinWaitlist}
              className="btn-gold px-10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest" 
              data-testid="waitlist-button"
            >
              Join Waitlist
            </button>
          </div>
          
          <div className="pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-[10px] text-slate-600 font-black uppercase tracking-[0.2em]">
            <p>© 2026 Ariv Logic. THE OFFICE THAT ACTS.</p>
            <div className="flex space-x-12 mt-6 md:mt-0">
              <a href="#" className="hover:text-gold transition" data-testid="linkedin-link">LinkedIn</a>
              <a href="mailto:bala@arivlogic.com" className="hover:text-gold transition" data-testid="founder-link">Founder Office</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;
